import { useMemo, useState } from "preact/hooks"
import { Button } from "../../../components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Label } from "../../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { AutocompleteSelect, SelectItem as AccountItem } from "../../../accounting/autocomplete_select"
import { accrual_bucket, added_row, bank_account } from "./accrual_bank_model"

/**
 * Start a GL account × customer row on the bank so its months can be typed in, without an
 * upload. Nothing is saved until the first amount is.
 */
export function AccrualAddRowModal({
  accounts,
  buckets,
  fixed_account_id,
  existing,
  onClose,
  onAdd,
}: {
  accounts: bank_account[]
  buckets: accrual_bucket[]
  /** When set, the GL is fixed (the "Add customer" link under a GL row). */
  fixed_account_id: string | null
  existing: added_row[]
  onClose: () => void
  onAdd: (account_id: string, bucket_id: string) => void
}) {
  const [account_id, setAccountId] = useState<string | null>(fixed_account_id)
  const [bucket_id, setBucketId] = useState<string>("")

  const account_items: AccountItem[] = useMemo(
    () => accounts.filter((a) => !a.hidden).map((a) => ({ id: a.id, name: a.name, expense_account: a.name, account_number: a.account_number ?? undefined })),
    [accounts]
  )
  const fixed = fixed_account_id ? accounts.find((a) => a.id === fixed_account_id) : null
  const available = buckets.filter((b) => !existing.some((e) => e.account_id === account_id && e.bucket_id === b.id))

  return (
    <Dialog open onOpenChange={(is_open: boolean) => !is_open && onClose()}>
      <DialogContent
        className="max-w-md"
        onInteractOutside={(e: Event) => {
          const target = e.target as HTMLElement
          if (target.closest("[data-radix-popper-content-wrapper]") || target.closest('[role="listbox"]') || target.closest("[cmdk-list]") || target.closest("[cmdk-input]") || target.closest("[cmdk-item]")) {
            e.preventDefault()
          }
        }}>
        <DialogHeader>
          <DialogTitle>{fixed ? `Add a customer to ${fixed.label}` : "Add a row"}</DialogTitle>
          <DialogDescription>Pick the GL account and customer bucket; then type the months' accruals straight into the table.</DialogDescription>
        </DialogHeader>
        <div class="space-y-3">
          {!fixed && (
            <div>
              <Label>GL account</Label>
              <AutocompleteSelect
                values={account_items}
                insideModal={true}
                searchPlaceholder="Search accounts…"
                emptyText="No account found."
                onSelect={(item: AccountItem) => setAccountId(item.id)}
              />
            </div>
          )}
          <div>
            <Label>Customer bucket</Label>
            <Select value={bucket_id} onValueChange={(v: string) => setBucketId(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a customer" />
              </SelectTrigger>
              <SelectContent>
                {available.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.rollup} / {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {available.length === 0 && <p class="mt-1 text-xs text-gray-500">Every bucket already has a row under this account.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!account_id || !bucket_id} onClick={() => account_id && bucket_id && onAdd(account_id, bucket_id)}>
            Add row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
